import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { 
  Truck, 
  MapPin, 
  CheckCircle2, 
  ArrowRight,
  Wifi,
  Zap,
  Shield,
  Clock
} from 'lucide-react';

const vanFeatures = [
  { icon: Wifi, title: 'Connected Systems', description: 'Real-time data sync to central platform' },
  { icon: Shield, title: 'Climate Controlled', description: 'Precision temperature regulation' },
  { icon: Zap, title: 'Self-Powered', description: 'Independent operation anywhere' },
  { icon: Clock, title: 'Rapid Deployment', description: '24-hour setup capability' },
];

const coverage = [
  { region: 'West Coast', cities: 35, vans: 15 },
  { region: 'Midwest', cities: 28, vans: 10 },
  { region: 'East Coast', cities: 40, vans: 14 },
  { region: 'Southwest', cities: 17, vans: 6 },
];

export const MobileVans = () => {
  return (
    <div className="bg-zinc-950 min-h-screen pt-20">
      {/* Hero */}
      <section className="py-32 relative overflow-hidden" data-testid="vans-hero">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-[150px]" />
        
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="overline mb-6 block">Mobile Units</span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
                Healthcare that<br />
                <span className="gradient-text">comes to you</span>
              </h1>
              <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-10">
                State-of-the-art mobile health units bringing comprehensive screening 
                directly to your workplace.
              </p>
              <Link to="/request-screening" data-testid="vans-cta-primary">
                <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                  Schedule a Unit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="aspect-square rounded-3xl overflow-hidden bg-zinc-900 border border-white/10">
                <img
                  src="https://images.pexels.com/photos/9458996/pexels-photo-9458996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern minimalist architecture"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-zinc-900 border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Truck className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">45</p>
                    <p className="text-xs text-zinc-500">Units Nationwide</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 border-t border-b border-white/5" data-testid="vans-stats">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: 45, suffix: '', label: 'Mobile Units' },
              { value: 120, suffix: '+', label: 'Cities' },
              { value: 500, suffix: '+', label: 'Clients' },
              { value: 99, suffix: '%', label: 'On-Time' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`van-stat-${index}`}
              >
                <p className="text-4xl md:text-5xl font-bold text-white font-['JetBrains_Mono']">
                  <CountUp end={stat.value} duration={2} enableScrollSpy scrollSpyOnce />
                  <span className="text-emerald-500">{stat.suffix}</span>
                </p>
                <p className="text-zinc-500 mt-2 text-sm uppercase tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-32" data-testid="van-features">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="overline mb-4 block">Capabilities</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tighter">
              Fully equipped facilities
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vanFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8"
                data-testid={`van-feature-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-5 h-5 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{feature.title}</h3>
                <p className="text-zinc-500 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-32 bg-zinc-900/30" data-testid="coverage-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="overline mb-4 block">Coverage</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 font-['Outfit'] tracking-tighter">
                Nationwide service
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Our fleet of 45 mobile units serves over 120 cities across the country.
              </p>

              <div className="space-y-4">
                {coverage.map((region, index) => (
                  <motion.div
                    key={region.region}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between bg-zinc-900/50 border border-white/5 rounded-xl p-4"
                    data-testid={`coverage-region-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-emerald-500" />
                      <span className="text-white font-medium">{region.region}</span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-zinc-500">{region.cities} cities</span>
                      <span className="text-emerald-500 font-['JetBrains_Mono']">{region.vans} units</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-3xl p-10 border border-emerald-500/20"
            >
              <h3 className="text-2xl font-bold text-white mb-6 font-['Outfit']">Why Mobile?</h3>
              <ul className="space-y-4">
                {[
                  'Zero employee travel time',
                  'Minimal workplace disruption',
                  'Professional medical staff',
                  'Immediate results',
                ].map((benefit, index) => (
                  <li key={benefit} className="flex items-center gap-3" data-testid={`van-benefit-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-zinc-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/5" data-testid="vans-cta">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Outfit'] tracking-tighter">
              Bring healthcare to your team
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Schedule a mobile unit and make preventative care accessible.
            </p>
            <Link to="/request-screening" data-testid="vans-final-cta">
              <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                Request a Unit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
